import { useState } from "react";
import { ZoneChip } from "@/components/layout/ZoneChip";
import { SiteMap } from "@/components/layout/SiteMap";
import { Link } from "react-router-dom";
import { ArrowUpRight, Menu, Store, X } from "lucide-react";
import { useFeature } from "@/context/SiteModeContext";

const NAV = [
    ["What you get", "#what-you-get"],
    ["Wizard & Consignment", "#modes"],
    ["The directory", "#directory"],
    ["Pricing", "#pricing"],
    ["Book a call", "#book-a-call"],
];

const GREEN = "#2563EB";

export const Mark = ({ size = 30 }) => (
    <span className="inline-flex items-center gap-2">
        <span className="grid place-items-center font-display font-black text-white"
            style={{ width: size, height: size, background: "#0b0b0b", borderRadius: 8, fontSize: size * 0.45 }}>13</span>
        <span className="font-display text-[17px] font-extrabold tracking-tight text-zinc-950">
            13thav<span style={{ color: "#1E40AF" }}>.</span>
        </span>
    </span>
);

/**
 * The product page wears its own clothes: white paper, near-black type, the shop green used
 * like a highlighter and nothing else. No mall map, no floor plan, no directory — that lives
 * one quiet link away.
 *
 * `at` prefixes the in-page anchors, so signing up (a different route) can wear this same
 * header and still get back to the pitch. `local` adds the Brooklyn shop button for walk-in
 * customers who landed here looking to buy, not to rent.
 */
export default function ProductChrome({ children, local, at = "", loginNext }) {
    const directoryOn = useFeature("directory");
    const [open, setOpen] = useState(false);
    const shop = local?.live ? local : null;
    return (
        <div className="pitch-floor min-h-screen bg-white text-zinc-950 antialiased selection:bg-[#2563EB]/30"
            data-testid="product-page">
            <header className="sticky top-0 z-50 border-b border-zinc-200/80 bg-white/85 backdrop-blur-xl"
                data-testid="product-header">
                <div className="mx-auto flex h-16 max-w-6xl items-center gap-6 px-5 sm:px-8">
                    <Link to="/" data-testid="product-logo"><Mark /></Link>
                    <ZoneChip zone="white-label" className="hidden xl:inline-flex" />
                    <nav className="hidden items-center gap-5 lg:flex">
                        {NAV.map(([label, href]) => (
                            <a key={href} href={`${at}${href}`} data-testid={`product-nav-${href.slice(1)}`}
                                className="whitespace-nowrap text-[13.5px] font-medium text-zinc-600 transition-colors hover:text-zinc-950">
                                {label}
                            </a>
                        ))}
                    </nav>
                    <div className="ml-auto flex items-center gap-2 sm:gap-3">
                        {shop && (
                            <Link to={shop.url} data-testid="chrome-local-shop"
                                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-zinc-300 px-3 py-1.5 text-[12.5px] font-semibold text-zinc-800 transition-colors hover:border-zinc-950 hover:text-zinc-950 sm:px-3.5 sm:py-2 sm:text-[13px]">
                                <Store size={13} />
                                <span className="sm:hidden">Shop {shop.city}</span>
                                <span className="hidden sm:inline">Shop our {shop.city} sign shop</span>
                            </Link>
                        )}
                        <div className="hidden items-center gap-3 md:flex">
                            <Link to={`/login?next=${encodeURIComponent(loginNext || "/account")}`} data-testid="product-login"
                                className="whitespace-nowrap text-[13.5px] font-semibold text-zinc-700 transition-colors hover:text-zinc-950">Log in</Link>
                            <a href={`${at}#pricing`} data-testid="product-header-cta"
                                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-zinc-950 px-4 py-2 text-[13px] font-semibold text-white transition-transform duration-200 hover:-translate-y-px">
                                Start your shop
                                <span className="h-1.5 w-1.5 rounded-full" style={{ background: GREEN }} />
                            </a>
                        </div>
                    </div>
                    <button onClick={() => setOpen((v) => !v)} data-testid="product-menu-toggle"
                        className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-zinc-200 md:hidden">
                        {open ? <X size={17} /> : <Menu size={17} />}
                    </button>
                </div>
                {open && (
                    <div className="border-t border-zinc-200 bg-white px-5 py-3 md:hidden" data-testid="product-mobile-nav">
                        {NAV.map(([label, href]) => (
                            <a key={href} href={`${at}${href}`} onClick={() => setOpen(false)}
                                className="block py-2.5 text-[15px] font-medium text-zinc-800">{label}</a>
                        ))}
                        {shop && (
                            <Link to={shop.url} onClick={() => setOpen(false)}
                                className="block py-2.5 text-[15px] font-bold text-zinc-950">Shop our {shop.city} store</Link>
                        )}
                        {directoryOn && <Link to="/directory" onClick={() => setOpen(false)}
                            className="block py-2.5 text-[15px] font-medium text-zinc-800">Browse directory</Link>}
                        <Link to={`/login?next=${encodeURIComponent(loginNext || "/account")}`} onClick={() => setOpen(false)}
                            className="block py-2.5 text-[15px] font-medium text-zinc-800">Log in</Link>
                    </div>
                )}
            </header>

            {children}

            <footer className="border-t border-zinc-200 bg-white" data-testid="product-footer">
                <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-7 gap-y-3 px-5 py-9 sm:px-8">
                    <Mark size={26} />
                    <span className="text-[13px] text-zinc-500">Brooklyn, since 2012</span>
                    <a href={`${at}#pricing`} className="text-[13px] font-medium text-zinc-700 hover:text-zinc-950">Pricing</a>
                    <a href={`${at}#see-it-live`} className="text-[13px] font-medium text-zinc-700 hover:text-zinc-950">See it live</a>
                    {directoryOn && <Link to="/directory" data-testid="product-footer-directory"
                        className="inline-flex items-center gap-1 text-[13px] font-medium text-zinc-700 hover:text-zinc-950">
                        Browse the directory <ArrowUpRight size={12} />
                    </Link>}
                    {shop && (
                        <Link to={shop.url} data-testid="product-footer-local"
                            className="inline-flex items-center gap-1 text-[13px] font-medium text-zinc-700 hover:text-zinc-950">
                            Shop {shop.name} <ArrowUpRight size={12} />
                        </Link>
                    )}
                    <Link to="/track" className="text-[13px] font-medium text-zinc-700 hover:text-zinc-950">Track an order</Link>
                    <a href="tel:+13475733875" className="ml-auto text-[13px] font-semibold text-zinc-950">(347) 573-3875</a>
                </div>
            </footer>
            <SiteMap />
        </div>
    );
}
